import React, { useState } from 'react';
import {
  FileText,
  CheckCircle,
  XCircle,
  Calendar,
  Plus,
  Edit,
  Trash2,
  Eye,
  Loader2,
  ListChecks,
} from 'lucide-react';
import { Button, SearchSelect } from '../../components/shared';
import DataTable from '../../components/shared/DataTable';
import { useAdminTermsPrivacy } from '../../hooks/useAdminTermsPrivacy';
import { formatDate } from '../../utils/dateUtils';
import { AdminPageHeader, AdminGuideCallout } from '../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../utils/adminPageGuides';

const AdminTermsPrivacy = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const {
    documents,
    isLoading,
    error,
    filters,
    documentTypeOptions,
    activeStatusOptions,
    handleFilterChange,
    handleSearch,
    statistics: stats
  } = useAdminTermsPrivacy();

  const columns = [
    {
      key: 'document_type',
      label: 'Type',
      sortable: true,
      width: 'w-40',
      render: (doc) => (
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100 capitalize">
          {doc.document_type?.replace('_', ' ') || 'N/A'}
        </span>
      )
    },
    {
      key: 'title',
      label: 'Title',
      sortable: true,
      width: 'w-64',
      render: (doc) => (
        <p className="text-sm font-medium text-gray-900 dark:text-gray-50 truncate">
          {doc.title || 'N/A'}
        </p>
      )
    },
    {
      key: 'content',
      label: 'Content Preview',
      sortable: false,
      width: 'w-96',
      render: (doc) => (
        <p className="text-sm text-gray-900 dark:text-gray-100 truncate">
          {doc.content?.substring(0, 100) || 'N/A'}...
        </p>
      )
    },
    {
      key: 'version',
      label: 'Version',
      sortable: true,
      width: 'w-24',
      render: (doc) => (
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
          {doc.version || '1.0'}
        </span>
      )
    },
    {
      key: 'effective_date',
      label: 'Effective Date',
      sortable: true,
      width: 'w-32',
      render: (doc) => (
        doc.effective_date ? (
          <div className="flex items-center space-x-2">
            <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-medium text-gray-900 dark:text-gray-50">
                {formatDate(doc.effective_date, 'short')}
              </div>
            </div>
          </div>
        ) : (
          <span className="text-sm text-gray-400">N/A</span>
        )
      )
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      width: 'w-32',
      render: (doc) => (
        <div className="flex items-center space-x-2">
          <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-medium text-gray-900 dark:text-gray-50">
              {formatDate(doc.created_at, 'medium')}
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'is_active',
      label: 'Status',
      sortable: true,
      width: 'w-24',
      render: (doc) => (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
          doc.is_active
            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300'
            : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
        }`}>
          {doc.is_active ? 'Active' : 'Inactive'}
        </span>
      )
    }
  ];

  const actions = [
    {
      label: 'View',
      icon: Eye,
      variant: 'default',
      onClick: (doc) => {
        console.log('View document:', doc);
      }
    },
    {
      label: 'Edit',
      icon: Edit,
      variant: 'default',
      onClick: (doc) => {
        setEditingDocument(doc);
        setShowCreateModal(true);
      }
    },
    {
      label: 'Delete',
      icon: Trash2,
      variant: 'danger',
      onClick: (doc) => {
        if (window.confirm('Are you sure you want to delete this document?')) {
          // deleteDocument(doc.id);
        }
      }
    }
  ];

  if (error) {
    return (
      <div className="space-y-5">
        <AdminPageHeader
          title="Terms & Privacy"
          subtitle="Manage terms of service, privacy policies, and legal documents"
        />
        <div className="rounded-2xl border border-red-200 dark:border-red-800/50 bg-white dark:bg-gray-900 p-6 text-center shadow-sm">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 dark:text-gray-50 mb-2">Error Loading Documents</h3>
          <p className="text-gray-600 dark:text-gray-400">
            {error?.response?.data?.message || 'Failed to load documents. Please try again later.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <AdminPageHeader
        title="Terms & Privacy"
        subtitle="Manage terms of service, privacy policies, and legal documents"
        actions={
          <Button
            className="!rounded-xl flex items-center gap-2"
            onClick={() => {
              setEditingDocument(null);
              setShowCreateModal(true);
            }}
          >
            <Plus className="w-4 h-4" />
            Create Document
          </Button>
        }
      />

      <AdminGuideCallout
        title={ADMIN_PAGE_GUIDES.termsPrivacy.title}
        steps={ADMIN_PAGE_GUIDES.termsPrivacy.steps}
        icon={ListChecks}
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-2xl border border-gray-200/80 bg-white dark:border-gray-700 dark:bg-gray-900 shadow-sm p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Documents</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-50 mt-1 tabular-nums">{stats.total_documents || 0}</p>
            </div>
            <FileText className="w-8 h-8 text-gray-400" />
          </div>
        </div>
        <div className="rounded-2xl border border-gray-200/80 bg-white dark:border-gray-700 dark:bg-gray-900 shadow-sm p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Active</p>
              <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 mt-1 tabular-nums">{stats.active_documents || 0}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-emerald-500" />
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200/80 bg-white dark:border-gray-700 dark:bg-gray-900 shadow-sm overflow-hidden">
        <div className="p-4 sm:p-5 space-y-5">
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-950/40 p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <SearchSelect
                label="Document Type"
                value={filters.document_type}
                onChange={(value) => handleFilterChange('document_type', value)}
                options={documentTypeOptions}
              />
              <SearchSelect
                label="Status"
                value={filters.is_active}
                onChange={(value) => handleFilterChange('is_active', value)}
                options={activeStatusOptions}
              />
            </div>
          </div>

          {isLoading ? (
            <div className="py-20 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading documents…</p>
            </div>
          ) : (
            <DataTable
              data={documents}
              columns={columns}
              actions={actions}
              isLoading={false}
              searchPlaceholder="Search by title, content, or document type..."
              onSearch={handleSearch}
              emptyMessage="No documents found"
              className="!border-0 !shadow-none"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminTermsPrivacy;
